import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, Sparkles, CheckCircle2, ArrowRight, Calculator, Plus, FolderKanban } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { SECTIONS, PRIORITY, navTestId } from './navConfig';
import { projectImage, projectStatus } from '../../lib/projectVisuals';

const NavItem = ({ item, active, collapsed, badge, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={navTestId(item.label)}
      title={collapsed ? item.label : undefined}
      className={`group relative flex items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-colors duration-150 ${
        active ? 'bg-[#F26B1F] text-white shadow-[0_10px_24px_-14px_rgba(242,107,31,1)]' : 'text-[#B9B4AC] hover:bg-white/[0.06] hover:text-white'
      } ${collapsed ? 'justify-center px-0' : ''}`}
    >
      <span className="relative flex-shrink-0">
        <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
        {badge > 0 && collapsed && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" data-testid={`nav-badge-dot-${item.badgeKey}`} />}
      </span>
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {!collapsed && badge > 0 && (
        <span className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full inline-flex items-center justify-center" data-testid={`nav-badge-${item.badgeKey}`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
};

const RecentProjects = ({ projects, summaries, onNavigate, activeId }) => (
  <div className="mt-6" data-testid="sidebar-recent-projects">
    <p className="px-3 mb-2 text-[11px] font-semibold text-[#8B857C]">Senaste projekt</p>
    <div className="space-y-1.5">
      {projects.map((p) => {
        const st = projectStatus(p);
        const s = summaries[p.id];
        return (
          <Link key={p.id} to={`/dashboard/projects/${p.id}`} onClick={onNavigate} data-testid={`sidebar-project-${p.id}`}
            className={`flex items-center gap-3 rounded-xl p-2 transition-colors ${activeId === p.id ? 'bg-white/[0.07] ring-1 ring-[#F26B1F]/60' : 'hover:bg-white/[0.05]'}`}>
            <img src={projectImage(p, s)} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" loading="lazy" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-white truncate">{p.name}</p>
              <p className="text-[11px] text-[#8B857C] truncate">{p.location || p.customer || (s?.total_area_m2 ? `${Math.round(s.total_area_m2)} m²` : 'Ny kalkyl')}</p>
              <span className={`mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  </div>
);

const MainFeature = ({ materialPath, onMaterial, projectsActive, onNavigate, onStart }) => (
  <div className="relative border border-[#F26B1F]/45 bg-gradient-to-b from-[#F26B1F]/[0.16] to-[#F26B1F]/[0.02] p-3" data-testid="sidebar-main-feature">
    <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#F26B1F]" />
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FF8A4C]">Huvudfunktion</p>
    <Link to={materialPath} onClick={onNavigate} data-testid="nav-materialberakning"
      className={`mt-2 flex items-center gap-3 p-2 -mx-1 transition-colors ${onMaterial ? 'bg-[#F26B1F] text-white' : 'hover:bg-white/[0.06]'}`}>
      <span className={`w-10 h-10 flex items-center justify-center flex-shrink-0 text-white ${onMaterial ? 'bg-white/20' : 'bg-[#F26B1F]'}`}><Calculator size={19} /></span>
      <span className="min-w-0">
        <span className="block text-[15px] font-bold text-white leading-tight">Materialberäkning</span>
        <span className={`block text-[11px] ${onMaterial ? 'text-white/80' : 'text-[#A39D94]'}`}>Ritning → 3D → kalkyl</span>
      </span>
    </Link>
    <Link to="/dashboard/projects" onClick={onNavigate} data-testid="nav-projekt"
      className={`mt-1 flex items-center gap-3 px-2 py-2 -mx-1 text-[13px] font-semibold transition-colors ${projectsActive ? 'bg-white/[0.1] text-white' : 'text-[#CFCAC2] hover:bg-white/[0.06] hover:text-white'}`}>
      <FolderKanban size={17} className="text-[#FF8A4C]" /><span className="flex-1">Projekt</span><ArrowRight size={13} className="text-[#6E6962]" />
    </Link>
    <button onClick={onStart} data-testid="sidebar-new-calc-btn"
      className="mt-2 w-full h-9 bg-[#F26B1F] hover:bg-[#e05a10] text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors">
      <Plus size={14} />Ny beräkning
    </button>
  </div>
);

const AICard = ({ onStart }) => (
  <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#1B1918] p-4" data-testid="sidebar-ai-card">
    <div className="flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-lg bg-[#F26B1F] text-white flex items-center justify-center"><Sparkles size={16} /></span>
      <p className="text-[13px] font-semibold text-white flex-1">AI-baserad beräkning</p>
      <span className="text-[10px] font-bold rounded-md bg-[#F26B1F]/20 text-[#FF8A4C] px-1.5 py-0.5">Beta</span>
    </div>
    <p className="mt-2.5 text-[11.5px] leading-relaxed text-[#A39D94]">AI läser ritningar, räknar mängder och ger en komplett material-, arbets- och tidskalkyl.</p>
    <ul className="mt-3 space-y-1.5 text-[11.5px] text-[#CFCAC2]">
      {['Ritningar analyseras', 'Mängder beräknas', '3D-modell skapas'].map((t) => (
        <li key={t} className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-400" />{t}</li>
      ))}
    </ul>
    <button onClick={onStart} data-testid="sidebar-start-calc-btn"
      className="mt-4 w-full h-9 rounded-xl border border-white/15 text-[12.5px] font-semibold text-white hover:bg-white/[0.06] transition-colors inline-flex items-center justify-center gap-1.5">
      Starta ny beräkning <ArrowRight size={13} />
    </button>
  </div>
);

export const Sidebar = ({ open, collapsed, setCollapsed, onClose, isActive, badges, recent, summaries, activeProjectId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const onMaterial = !!activeProjectId && new URLSearchParams(location.search).get('tab') === 'material-plan';
  const materialPath = recent[0] ? `/dashboard/projects/${recent[0].id}?tab=material-plan` : '/dashboard/projects?new=1';
  const startCalc = () => { onClose(); navigate('/dashboard/projects?new=1'); };
  return (
    <aside
      className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-[#121110] text-white transition-[width,transform] duration-300 ${
        open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} ${collapsed ? 'w-[76px]' : 'w-[264px]'}`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="admin-sidebar"
    >
      <div className="flex items-center justify-between h-16 px-5 flex-shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <img src="/operio-logo-2.png" alt="Operio" className="w-8 h-8" />
          {!collapsed && <span className="text-[22px] font-bold tracking-tight">Operio</span>}
        </Link>
        <button onClick={onClose} className="lg:hidden p-1 text-[#8B857C] hover:text-white" data-testid="close-sidebar-btn"><X size={18} /></button>
        <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex p-1 text-[#8B857C] hover:text-white" data-testid="collapse-sidebar-btn">
          <ChevronLeft size={16} className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto op-noscroll px-3 pb-4" data-testid="sidebar-nav">
        {collapsed ? (
          <div className="space-y-1 mt-1" data-testid="sidebar-priority">
            <NavItem item={{ path: materialPath, label: 'Materialberäkning', icon: Calculator }} active={onMaterial} collapsed badge={0} onClick={onClose} />
            {PRIORITY.map((item) => (
              <NavItem key={item.path} item={item} active={isActive(item.path) && !onMaterial} collapsed badge={0} onClick={onClose} />
            ))}
          </div>
        ) : (
          <div className="mt-1" data-testid="sidebar-priority">
            <MainFeature materialPath={materialPath} onMaterial={onMaterial} projectsActive={isActive('/dashboard/projects') && !onMaterial}
              onNavigate={onClose} onStart={startCalc} />
            <div className="mt-2"><NavItem item={PRIORITY[0]} active={isActive(PRIORITY[0].path)} collapsed={false} badge={0} onClick={onClose} /></div>
          </div>
        )}
        {collapsed ? (
          <div className="mt-4 space-y-0.5 border-t border-white/[0.06] pt-4">
            {SECTIONS.flatMap((sec) => sec.items).map((item) => (
              <NavItem key={item.path} item={item} active={isActive(item.path)} collapsed badge={item.badgeKey ? badges[item.badgeKey] || 0 : 0} onClick={onClose} />
            ))}
          </div>
        ) : (
          <Accordion type="multiple" defaultValue={[]} className="mt-3 border-t border-white/[0.06] pt-2" data-testid="sidebar-accordion">
            {SECTIONS.map((section) => {
              const count = section.items.reduce((n, it) => n + (it.badgeKey ? badges[it.badgeKey] || 0 : 0), 0);
              return (
                <AccordionItem key={section.title} value={section.title} className="border-0">
                  <AccordionTrigger data-testid={`sidebar-section-${section.title.toLowerCase()}`}
                    className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B857C] hover:text-white hover:no-underline rounded-md [&>svg]:text-[#6E6962]">
                    <span className="flex items-center gap-2">{section.title}{count > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#F26B1F]" />}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-2 space-y-0.5">
                    {section.items.map((item) => (
                      <NavItem key={item.path} item={item} active={isActive(item.path)} collapsed={false}
                        badge={item.badgeKey ? badges[item.badgeKey] || 0 : 0} onClick={onClose} />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
        {!collapsed && recent.length > 0 && (
          <RecentProjects projects={recent} summaries={summaries} onNavigate={onClose} activeId={activeProjectId} />
        )}
        {!collapsed && <AICard onStart={startCalc} />}
      </nav>
      {!collapsed && (
        <div className="relative h-20 flex-shrink-0 overflow-hidden border-t border-white/[0.06]">
          <div className="absolute inset-0 bg-gradient-to-t from-[#F26B1F]/25 via-[#F26B1F]/5 to-transparent op-glow" />
          <div className="relative h-full px-5 flex items-center gap-2.5">
            <img src="/operio-logo-2.png" alt="" className="w-6 h-6" />
            <p className="text-[12.5px] font-semibold text-white/90">Bygg smartare. Med Operio.</p>
          </div>
        </div>
      )}
    </aside>
  );
};
